import React from 'react';
import { useLocation, Link } from 'react-router-dom';

const PrivacyPolicy = () => {
    const location = useLocation();
    const hash = location.hash;

    // Scroll to section on hash change
    React.useEffect(() => {
        if (hash) {
            const element = document.getElementById(hash.replace('#', ''));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }, [hash]);

    const styles = {
        container: {
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
            color: '#e2e8f0',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
        },
        wrapper: {
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '4rem 2rem'
        },
        header: {
            textAlign: 'center',
            marginBottom: '3rem'
        },
        logo: {
            fontSize: '2rem',
            fontWeight: 'bold',
            color: '#6366f1',
            marginBottom: '2rem',
            textDecoration: 'none',
            display: 'inline-block'
        },
        title: {
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '800',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
        },
        subtitle: {
            color: '#94a3b8',
            fontSize: '1.2rem',
            maxWidth: '600px',
            margin: '0 auto 2rem'
        },
        quickLinks: {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '3rem'
        },
        quickLink: {
            background: 'rgba(99, 102, 241, 0.1)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            padding: '0.75rem 1.5rem',
            borderRadius: '2rem',
            color: '#a5b4fc',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            fontSize: '0.95rem',
            fontWeight: '500'
        },
        sectionsGrid: {
            display: 'grid',
            gap: '2.5rem'
        },
        section: {
            background: 'rgba(30, 41, 59, 0.6)',
            backdropFilter: 'blur(20px)',
            padding: '2.5rem',
            borderRadius: '1.5rem',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        },
        sectionHeader: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '2rem'
        },
        sectionIndicator: (color) => ({
            width: '6px',
            height: '40px',
            background: `linear-gradient(180deg, ${color} 0%, ${color}80 100%)`,
            borderRadius: '3px',
            marginRight: '1rem',
            boxShadow: `0 0 20px ${color}40`
        }),
        sectionTitle: {
            fontSize: '1.75rem',
            color: '#fff',
            margin: 0,
            fontWeight: '700'
        },
        sectionContent: {
            color: '#cbd5e1',
            lineHeight: '1.8'
        },
        lastUpdated: {
            display: 'inline-block',
            background: 'rgba(99, 102, 241, 0.1)',
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '0.9rem',
            color: '#a5b4fc',
            marginBottom: '1.5rem'
        },
        subheading: {
            color: '#fff',
            fontSize: '1.1rem',
            fontWeight: '600',
            marginTop: '2rem',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center'
        },
        subheadingIcon: {
            marginRight: '0.5rem',
            fontSize: '1.2rem'
        },
        paragraph: {
            marginBottom: '1rem',
            color: '#cbd5e1'
        },
        list: {
            paddingLeft: '1.5rem',
            marginBottom: '1rem'
        },
        listItem: {
            marginBottom: '0.5rem',
            color: '#94a3b8'
        },
        footer: {
            textAlign: 'center',
            marginTop: '4rem',
            padding: '2rem',
            borderTop: '1px solid rgba(255,255,255,0.1)'
        },
        footerText: {
            color: '#64748b',
            marginBottom: '1rem'
        },
        contactLink: {
            color: '#6366f1',
            textDecoration: 'none'
        },
        backButton: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: '#fff',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.75rem',
            textDecoration: 'none',
            fontWeight: '500',
            marginTop: '1rem',
            transition: 'transform 0.2s ease'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.wrapper}>
                {/* Header */}
                <div style={styles.header}>
                    <Link to="/" style={styles.logo}>
                        📞 Ringez
                    </Link>
                    <h1 style={styles.title}>Legal Center</h1>
                    <p style={styles.subtitle}>
                        Transparency is our core value. Here's everything you need to know about how we operate and protect your interests.
                    </p>

                    {/* Quick Navigation */}
                    <div style={styles.quickLinks}>
                        <a href="#privacy" style={styles.quickLink}>🔒 Privacy Policy</a>
                        <a href="#terms" style={styles.quickLink}>📋 Terms & Conditions</a>
                        <a href="#refund" style={styles.quickLink}>💰 Refund Policy</a>
                    </div>
                </div>

                <div style={styles.sectionsGrid}>
                    {/* Privacy Policy Section */}
                    <section id="privacy" style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={styles.sectionIndicator('#6366f1')}></div>
                            <h2 style={styles.sectionTitle}>Privacy Policy</h2>
                        </div>
                        <div style={styles.sectionContent}>
                            <div style={styles.lastUpdated}>Last updated: January 1, 2026</div>

                            <p style={styles.paragraph}>
                                At Ringez, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, and safeguard your data.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>📊</span> 1. Information We Collect
                            </h3>
                            <p style={styles.paragraph}>We collect information you provide directly to us, including:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Personal identification information (name, email address, phone number)</li>
                                <li style={styles.listItem}>Account credentials and profile information</li>
                                <li style={styles.listItem}>Payment and transaction details</li>
                                <li style={styles.listItem}>Call records and usage data</li>
                                <li style={styles.listItem}>Device and browser information</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>🎯</span> 2. How We Use Your Information
                            </h3>
                            <p style={styles.paragraph}>We use the information we collect to:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Provide, maintain, and improve our calling services</li>
                                <li style={styles.listItem}>Process transactions and send related information</li>
                                <li style={styles.listItem}>Send you technical notices and support messages</li>
                                <li style={styles.listItem}>Respond to your comments, questions, and customer service requests</li>
                                <li style={styles.listItem}>Detect, prevent, and address technical issues and fraud</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>🔐</span> 3. Data Protection & Security
                            </h3>
                            <p style={styles.paragraph}>
                                We implement industry-standard security measures to protect your personal data. All data is encrypted in transit and at rest. We regularly review and update our security practices to maintain the highest levels of protection.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>🤝</span> 4. Data Sharing
                            </h3>
                            <p style={styles.paragraph}>
                                We do not sell, trade, or rent your personal information to third parties. We may share your information only with trusted service providers who assist us in operating our platform, subject to strict confidentiality agreements.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>🌍</span> 5. International Data Transfers
                            </h3>
                            <p style={styles.paragraph}>
                                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>✉️</span> 6. Contact Us
                            </h3>
                            <p style={styles.paragraph}>
                                If you have any questions about this Privacy Policy, please contact us at <a href="mailto:privacy@ringez.com" style={styles.contactLink}>privacy@ringez.com</a>
                            </p>
                        </div>
                    </section>

                    {/* Terms and Conditions Section */}
                    <section id="terms" style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={styles.sectionIndicator('#10b981')}></div>
                            <h2 style={styles.sectionTitle}>Terms and Conditions</h2>
                        </div>
                        <div style={styles.sectionContent}>
                            <div style={styles.lastUpdated}>Last updated: January 1, 2026</div>

                            <p style={styles.paragraph}>
                                Welcome to Ringez! These Terms and Conditions govern your use of our VoIP calling services. By accessing or using our service, you agree to be bound by these terms.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>📖</span> 1. Acceptance of Terms
                            </h3>
                            <p style={styles.paragraph}>
                                By creating an account or using Ringez services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions and our Privacy Policy.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>👤</span> 2. Account Registration
                            </h3>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>You must provide accurate and complete information when creating an account</li>
                                <li style={styles.listItem}>You are responsible for maintaining the security of your account credentials</li>
                                <li style={styles.listItem}>You must be at least 18 years old to use our services</li>
                                <li style={styles.listItem}>One person may not maintain more than one account</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>📞</span> 3. Service Usage
                            </h3>
                            <p style={styles.paragraph}>You agree to use our services only for lawful purposes. Prohibited activities include:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Making harassing, threatening, or fraudulent calls</li>
                                <li style={styles.listItem}>Using the service for illegal activities</li>
                                <li style={styles.listItem}>Attempting to interfere with or disrupt our services</li>
                                <li style={styles.listItem}>Reselling or redistributing our services without authorization</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>💳</span> 4. Payments and Billing
                            </h3>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>All payments are processed securely through our payment partners</li>
                                <li style={styles.listItem}>Credits added to your wallet are non-transferable</li>
                                <li style={styles.listItem}>Call rates are displayed before each call is initiated</li>
                                <li style={styles.listItem}>We reserve the right to modify pricing with prior notice</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>⚠️</span> 5. Limitation of Liability
                            </h3>
                            <p style={styles.paragraph}>
                                Ringez is not liable for any indirect, incidental, special, or consequential damages arising from your use of our services. Our total liability is limited to the amount you paid for the service in the preceding 12 months.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>🚫</span> 6. Emergency Services Disclaimer
                            </h3>
                            <p style={{ ...styles.paragraph, background: 'rgba(239, 68, 68, 0.1)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                                <strong>Important:</strong> Ringez is not intended for emergency calls. Our service does not support calls to emergency services (911, 112, 999, etc.). For emergencies, please use a traditional phone line or mobile device.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>🔄</span> 7. Modifications to Terms
                            </h3>
                            <p style={styles.paragraph}>
                                We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or through our platform. Continued use of our services after changes constitutes acceptance of the modified terms.
                            </p>
                        </div>
                    </section>

                    {/* Refund Policy Section */}
                    <section id="refund" style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <div style={styles.sectionIndicator('#f59e0b')}></div>
                            <h2 style={styles.sectionTitle}>Refund Policy</h2>
                        </div>
                        <div style={styles.sectionContent}>
                            <div style={styles.lastUpdated}>Last updated: January 1, 2026</div>

                            <p style={styles.paragraph}>
                                We want you to be completely satisfied with Ringez. This Refund Policy outlines the conditions under which refunds may be issued.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>✅</span> 1. Refund Eligibility
                            </h3>
                            <p style={styles.paragraph}>Refunds may be considered in the following circumstances:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Technical issues preventing service use that we cannot resolve</li>
                                <li style={styles.listItem}>Duplicate or erroneous charges</li>
                                <li style={styles.listItem}>Service outages affecting your ability to make calls</li>
                                <li style={styles.listItem}>Fraudulent account activity (subject to investigation)</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>❌</span> 2. Non-Refundable Items
                            </h3>
                            <p style={styles.paragraph}>The following are generally not eligible for refunds:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Used call credits (minutes already consumed)</li>
                                <li style={styles.listItem}>Partially used credit balances under $5</li>
                                <li style={styles.listItem}>Credits unused for more than 6 months</li>
                                <li style={styles.listItem}>Accounts terminated due to Terms of Service violations</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>📝</span> 3. How to Request a Refund
                            </h3>
                            <p style={styles.paragraph}>To request a refund:</p>
                            <ul style={styles.list}>
                                <li style={styles.listItem}>Contact our support team at <a href="mailto:support@ringez.com" style={styles.contactLink}>support@ringez.com</a></li>
                                <li style={styles.listItem}>Include your account email and transaction details</li>
                                <li style={styles.listItem}>Describe the reason for your refund request</li>
                                <li style={styles.listItem}>Submit your request within 30 days of the original purchase</li>
                            </ul>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>⏱️</span> 4. Processing Time
                            </h3>
                            <p style={styles.paragraph}>
                                Once approved, refunds are typically processed within 5-10 business days. The time for the refund to appear in your account may vary depending on your payment provider and bank.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>💱</span> 5. Refund Method
                            </h3>
                            <p style={styles.paragraph}>
                                Refunds will be issued to the original payment method used for the purchase. In some cases, we may offer account credits as an alternative.
                            </p>

                            <h3 style={styles.subheading}>
                                <span style={styles.subheadingIcon}>📧</span> 6. Contact Us
                            </h3>
                            <p style={styles.paragraph}>
                                For any questions about our Refund Policy, please reach out to our support team at <a href="mailto:support@ringez.com" style={styles.contactLink}>support@ringez.com</a>. We're here to help!
                            </p>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <p style={styles.footerText}>
                        © {new Date().getFullYear()} Ringez. All rights reserved.
                    </p>
                    <p style={styles.footerText}>
                        Questions? Contact us at <a href="mailto:legal@ringez.com" style={styles.contactLink}>legal@ringez.com</a>
                    </p>
                    <Link to="/call" style={styles.backButton}>
                        ← Back to App
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
